import { DateTime } from 'luxon';
import { NGXLogger } from 'ngx-logger';

import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { v4 } from 'uuid';
import { LoaderService } from './loader.service';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
  private static readonly REQUEST_ALIVE_TIME_SECONDS = 60;
  private reqMap = new Map<string, { deadline: Date; req: HttpRequest<any> }>();

  constructor(
    private loaderService: LoaderService,
    private logger: NGXLogger
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (globalThis.sigv.appContext.sigvRetailConfig.disableHttpInterceptor) {
      return next.handle(request);
    }

    if (request.responseType === 'blob') {
      return next.handle(request);
    }

    const key = v4();
    this.addToLoadingMap(key, request);
    let lastResponse: HttpEvent<any>;
    let error: HttpErrorResponse;
    return next
      .handle(request)
      .pipe(
        tap((response: HttpEvent<any>) => {
          lastResponse = response;
        }),
        catchError((err) => {
          error = err;
          this.removeFromLoadingMap(key);
          return throwError(err);
        }),
        finalize(() => {
          if (lastResponse.type === HttpEventType.Sent && !error) {
            // last response type was 0, and we haven't received an error
            this.logger.debug('aborted request', lastResponse);
            this.removeFromLoadingMap(key);
          }
        })
      )
      .pipe(
        map((event) => {
          if (event instanceof HttpResponse) {
            this.removeFromLoadingMap(key);
          }
          return event;
        })
      );
  }

  private addToLoadingMap(key: string, req: HttpRequest<any>): void {
    const deadline = DateTime.now()
      .plus({ seconds: LoaderInterceptor.REQUEST_ALIVE_TIME_SECONDS })
      .toJSDate();
    this.reqMap.set(key, { deadline, req });
    this.logger.debug(
      `Add to request queue (${this.reqMap.size}):`,
      Array.from(this.reqMap, ([key, value]) => ({ key, value }))
    );
    this.loaderService.show();
  }

  private removeFromLoadingMap(key: string): void {
    const now = new Date();
    this.reqMap.delete(key);
    this.reqMap.forEach((value, key) => {
      if (now.getTime() >= value.deadline.getTime()) {
        this.reqMap.delete(key);
      }
    });
    this.reqMap.size === 0
      ? this.loaderService.hide()
      : this.loaderService.show();
    this.logger.debug(
      `Request queue (${this.reqMap.size}):`,
      Array.from(this.reqMap, ([key, value]) => ({ key, value }))
    );
  }
}
